// Shared message thread — displays conversation, send box, read receipts
import { useState, useRef, useEffect } from "react";
import { Send, Check, CheckCheck } from "lucide-react";
import { formatDateTime } from "@/utils/format";
import type { Message } from "@/hooks/useMessages";

interface Props {
  messages: Message[];
  currentRole: "agent" | "owner";
  onSend: (content: string) => Promise<boolean>;
  sending?: boolean;
  readOnly?: boolean;
  onMarkRead?: () => void;
}

export function MessageThread({ messages, currentRole, onSend, sending, readOnly, onMarkRead }: Props) {
  const [draft, setDraft] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Mark messages as read when thread is opened
  useEffect(() => {
    if (onMarkRead && messages.some((m) => m.sender_role !== currentRole && !m.read_at)) {
      onMarkRead();
    }
  }, [messages, currentRole, onMarkRead]);

  const handleSend = async () => {
    const content = draft.trim();
    if (!content) return;
    const sent = await onSend(content);
    if (sent) setDraft("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" });
    return formatDateTime(d);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto space-y-3 p-3 min-h-[200px] max-h-[400px]">
        {messages.length === 0 ? (
          <div className="text-center py-8 text-white/30 text-sm">
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map((msg) => {
            const isSystem = msg.sender_role === "system";
            const isMine = !isSystem && msg.sender_role === currentRole;

            if (isSystem) {
              return (
                <div key={msg.id} className="flex justify-center">
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 text-center">
                    <p className="text-xs text-emerald-400">{msg.content}</p>
                    <span className="text-[10px] text-white/30">{formatTime(msg.created_at)}</span>
                  </div>
                </div>
              );
            }

            return (
              <div key={msg.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-lg px-3 py-2 ${isMine ? "bg-sun/20 text-white" : "bg-white/10 text-white/90"}`}>
                  <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                  <div className={`flex items-center gap-1 mt-1 ${isMine ? "justify-end" : "justify-start"}`}>
                    <span className="text-[10px] text-white/40">{formatTime(msg.created_at)}</span>
                    {isMine && (
                      msg.read_at ? (
                        <CheckCheck className="h-3 w-3 text-blue-400" />
                      ) : (
                        <Check className="h-3 w-3 text-white/30" />
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Send box */}
      {!readOnly ? (
        <div className="border-t border-white/10 p-3 flex gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 resize-none focus:outline-none focus:border-sun/50"
          />
          <button
            onClick={handleSend}
            disabled={!draft.trim() || sending}
            className="p-2 rounded-lg bg-sun/20 text-sun hover:bg-sun/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="border-t border-white/10 p-3 text-center text-white/30 text-xs">
          This conversation is read-only
        </div>
      )}
    </div>
  );
}
